import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Chip, 
  Button, 
  Card, 
  CardContent, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { 
  Edit as EditIcon, 
  ContentCopy as CloneIcon, 
  DeleteOutline as DeleteIcon, 
  PlayArrow as PlayIcon, 
  Pause as PauseIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  BarChart as BarChartIcon,
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useAutomations } from '../../hooks/useAutomations';
import apiClient from '../../api/client';
import useAuthToken from '../../hooks/useAuthToken';
import PerformanceMetrics from '../../components/automation/PerformanceMetrics';
import ExecutionHistory from '../../components/automation/ExecutionHistory';
import { useAutomationPerformance } from '../../hooks/useAutomationPerformance';
import DevOnly from '../../components/DevOnly';
import formatSchedule from '../../utils/formatSchedule';


// Enhanced function to convert Unlayer email builder JSON to HTML
const convertEmailJsonToHtml = (jsonContent) => {
  try {
    // Try to parse the JSON
    let emailData;
    if (typeof jsonContent === 'string') {
      emailData = JSON.parse(jsonContent);
    } else {
      emailData = jsonContent;
    }
    
    // Check if this is our expected email builder format (has body, rows, etc.)
    if (!emailData.body || !emailData.body.rows) {
      console.log('Email content is not in expected format');
      return null; // Not our expected format
    }
    
    // Try to find HTML content in the Unlayer format
    let foundHtml = null;
    for (const row of emailData.body.rows) {
      if (row.columns && Array.isArray(row.columns)) {
        for (const column of row.columns) {
          if (column.contents && Array.isArray(column.contents)) {
            for (const content of column.contents) {
              if (content.type === 'html' && content.values && content.values.html) {
                console.log('Found HTML content in Unlayer format');
                foundHtml = content.values.html;
                break;
              }
            }
          }
        }
      }
    }
    
    if (foundHtml) {
      return foundHtml;
    }
    
    // If we get here, we didn't find HTML in the expected format
    // Extract style information from the body values
    const bodyStyles = emailData.body.values || {};
    const fontFamily = bodyStyles.fontFamily?.value || 'arial,helvetica,sans-serif';
    const textColor = bodyStyles.textColor || '#000000';
    const backgroundColor = bodyStyles.backgroundColor || '#FFFFFF';
    
    // Start building the HTML (Fallback to simple structured HTML)
    let html = `
      <html>
      <head>
        <style>
          body {
            font-family: ${fontFamily};
            color: ${textColor};
            background-color: ${backgroundColor};
            margin: 0;
            padding: 20px;
          }
          .container {
            max-width: 600px;
            margin: 0 auto;
          }
          .row {
            margin-bottom: 20px;
          }
          .column {
            padding: 10px;
          }
          img {
            max-width: 100%;
          }
          a {
            color: ${bodyStyles.linkStyle?.linkColor || '#0000ee'};
            text-decoration: ${bodyStyles.linkStyle?.linkUnderline ? 'underline' : 'none'};
          }
        </style>
      </head>
      <body>
        <div class="container">
    `;
    
    // Process each row in the email
    emailData.body.rows.forEach(row => {
      html += '<div class="row">';
      
      // Process columns in this row
      if (row.columns && Array.isArray(row.columns)) {
        row.columns.forEach(column => {
          const columnStyles = column.values || {};
          const columnPadding = columnStyles.padding || '0px';
          const columnBgColor = columnStyles.backgroundColor || '';
          
          html += `<div class="column" style="padding: ${columnPadding}; background-color: ${columnBgColor}">`;
          
          // Process contents of this column
          if (column.contents && Array.isArray(column.contents)) {
            column.contents.forEach(content => {
              // Handle different content types
              if (content.type === 'text') {
                const textValues = content.values || {};
                const fontSize = textValues.fontSize || 'inherit';
                const lineHeight = textValues.lineHeight || 'normal';
                const textAlign = textValues.textAlign || 'left';
                const containerPadding = textValues.containerPadding || '0px';
                
                html += `
                  <div style="font-size: ${fontSize}; line-height: ${lineHeight}; text-align: ${textAlign}; padding: ${containerPadding}">
                    ${textValues.text || ''}
                  </div>
                `;
              } else if (content.type === 'image') {
                const imageValues = content.values || {};
                html += `
                  <div style="padding: ${imageValues.containerPadding || '0px'}; text-align: ${imageValues.imageAlign || 'center'}">
                    <img src="${imageValues.src}" alt="${imageValues.alt || ''}" style="width: ${imageValues.width || 'auto'}; height: ${imageValues.height || 'auto'}">
                  </div>
                `;
              } else if (content.type === 'button') {
                const buttonValues = content.values || {};
                html += `
                  <div style="padding: ${buttonValues.containerPadding || '10px'}; text-align: ${buttonValues.buttonAlign || 'center'}">
                    <a href="${buttonValues.href || '#'}" style="
                      display: inline-block;
                      padding: ${buttonValues.padding || '10px 20px'};
                      background-color: ${buttonValues.backgroundColor || '#000000'};
                      color: ${buttonValues.color || '#FFFFFF'};
                      text-decoration: none;
                      border-radius: ${buttonValues.borderRadius || '4px'};
                      font-size: ${buttonValues.fontSize || 'inherit'};
                    ">
                      ${buttonValues.text || 'Button'}
                    </a>
                  </div>
                `;
              }
              // Add other content types as needed
            });
          }
          
          html += '</div>'; // Close column
        });
      }
      
      html += '</div>'; // Close row
    });
    
    // Close the HTML
    html += `
        </div>
      </body>
      </html>
    `;
    
    return html;
  } catch (error) {
    console.error('Error converting email JSON to HTML:', error);
    return null;
  }
};

// Email Preview component
const EmailPreview = ({ htmlContent }) => {
  const [previewContent, setPreviewContent] = useState('');
  
  useEffect(() => {
    // Process the HTML content when it changes
    if (!htmlContent) {
      setPreviewContent('');
      return;
    }
    
    try {
      // Check if the content is JSON (from email builder like Unlayer)
      if (htmlContent.trim().startsWith('{') && htmlContent.trim().endsWith('}')) {
        // Try to parse as JSON and extract HTML
        try {
          const extractedHtml = convertEmailJsonToHtml(htmlContent);
          if (extractedHtml) {
            console.log('Successfully extracted HTML from template JSON');
            setPreviewContent(extractedHtml);
            return;
          } else {
            console.log('Failed to extract HTML, displaying formatted JSON');
            // If extraction fails, display formatted JSON
            const parsedJson = JSON.parse(htmlContent);
            const formattedJson = `
              <html>
                <head>
                  <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    pre { background-color: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap; }
                  </style>
                </head>
                <body>
                  <pre>${JSON.stringify(parsedJson, null, 2).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
                </body>
              </html>
            `;
            setPreviewContent(formattedJson);
            return;
          }
        } catch (jsonError) {
          console.warn('JSON parsing failed:', jsonError);
        }
      }
      
      // If content is not JSON or JSON parsing failed, treat as regular HTML
      // Check if it has basic HTML structure
      if (!htmlContent.includes('<html') && !htmlContent.includes('<body')) {
        // Wrap in basic HTML
        setPreviewContent(`
          <html>
            <head>
              <style>
                body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                img { max-width: 100%; height: auto; }
                a { color: #0066cc; text-decoration: underline; }
              </style>
            </head>
            <body>${htmlContent}</body>
          </html>
        `);
      } else {
        // Already has HTML structure
        setPreviewContent(htmlContent);
      }
      
    } catch (error) {
      console.error('Error processing email preview content:', error);
      setPreviewContent(`
        <html>
          <body>
            <div style="color: #ff0000; padding: 20px; font-family: Arial, sans-serif;">
              Error preparing email preview
            </div>
          </body>
        </html>
      `);
    }
  }, [htmlContent]);
  
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">Preview:</Typography>
      <Paper 
        variant="outlined" 
        sx={{ 
          mt: 1, 
          p: 0, // No padding for iframe container
          height: '300px', // Increased height for better visibility
          overflow: 'hidden',
          bgcolor: 'background.default' 
        }}
      >
        {htmlContent ? (
          <Box 
            component="iframe"
            sx={{ 
              width: '100%', 
              height: '100%', 
              border: 'none',
              overflow: 'auto'
            }}
            srcDoc={previewContent}
            title="Email Preview"
            sandbox="allow-same-origin allow-scripts"
          />
        ) : (
          <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary" variant="body2">No email content available</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const AutomationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useAuthToken();
  
  // Core hooks from original code
  const { getAutomation, toggleAutomation, deleteAutomation, cloneAutomation, isUpdating } = useAutomations();
  const { performance, isLoading: performanceLoading, error: performanceError } = useAutomationPerformance(id);

  
  // State variables
  const [loading, setLoading] = useState(true);
  const [automation, setAutomation] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [metrics, setMetrics] = useState({
    emailsSent: 0,
    openRate: 0,
    clickRate: 0,
    bounceRate: 0
  });
  const [error, setError] = useState(null);
  
  // Debug: Log automation data when it changes
  useEffect(() => {
    if (automation) {
      console.log('🔍 DETAILS.JS - Automation data debug:', {
        id: automation.id,
        title: automation.title,
        raw_schedule_time: automation.schedule_time,
        type: typeof automation.schedule_time
      });
    }
  }, [automation]);
  
  // Fetch audience conditions from the API
  const fetchAudienceConditions = async (audienceId, token) => {
    if (!audienceId) {
      console.warn('fetchAudienceConditions called with no audienceId');
      return null;
    }
    
    try {
      console.log(`Attempting to fetch audience conditions for ID: ${audienceId}`);
      
      // Try direct API call 
      const response = await apiClient.get(`/audience/conditions/${audienceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        console.log('SUCCESS! Audience conditions received:', response.data);
        return response.data;
      } else {
        console.warn('API call succeeded but returned no data');
      }
    } catch (err) {
      console.error('Error fetching audience conditions:', err);
      
      // Add more detailed error logging
      if (err.response) {
        console.error('Error status:', err.response.status);
        console.error('Error data:', err.response.data);
      } else {
        console.error('Error message:', err.message);
      }
      
      // Try a fallback query to check if audience exists
      try {
        console.log('Attempting fallback to verify audience exists...');
        const checkResponse = await apiClient.get(`/audiences/${audienceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Audience exists check result:', checkResponse.data);
      } catch (checkErr) {
        console.error('Audience existence check also failed:', checkErr);
      }
    }
    
    return null;
  };
  
  // Fetch automation details using the hook from original code
  useEffect(() => {
    const fetchAutomationDetails = async () => {
      try {
        setLoading(true);
        const data = await getAutomation(id);
        
        console.log('Received automation data:', data);
        
        // Keep the same data structure consistency
        if (data) {
          // Log the complete structure for debugging
          console.log('Full automation object:', JSON.stringify(data));
          console.log('Audience data:', data.audience);
          
          if (data.audience_id && !data.audience) {
            data.audience = {
              id: data.audience_id,
              name: 'Default Audience',
              filter_name: 'Default Filter'
            };
          }
        }
        
        setAutomation(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching automation details:', err);
        setError('Failed to load automation details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAutomationDetails();
  }, [id, getAutomation]);
  
  // Calculate audience size using API call from original code
  useEffect(() => {
    const getAudienceSize = async () => {
      if (!automation || !automation.audience) return;
      
      try {
        // Check if we have an audience ID
        const audienceId = automation.audience?.id || automation.audience_id;
        
        if (!audienceId) {
          console.log('No audience ID available for size estimation');
          return;
        }
        
        console.log(`Fetching audience size for audience ID: ${audienceId}`);
        
        // First try to use API to get real count
        try {
          const audienceData = await fetchAudienceConditions(audienceId, token);
          
          if (audienceData) {
            // Use the preview endpoint to get a count of matching recipients
            const previewResponse = await apiClient.post(
              '/audience/preview',
              audienceData,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (previewResponse.data && previewResponse.data.matched_count !== undefined) {
              console.log(`Actual audience size from API: ${previewResponse.data.matched_count}`);
              // Update automation data with the audience size
              setAutomation(prev => ({
                ...prev,
                audience: {
                  ...(prev.audience || {}),
                  estimated_count: previewResponse.data.matched_count
                }
              }));
              return;
            }
          }
        } catch (apiErr) {
          console.warn('Could not get audience size from API:', apiErr);
        }
        
        // If we already have an estimated_count, use that
        if (automation.audience?.estimated_count !== undefined) {
          console.log(`Using estimated count from data: ${automation.audience.estimated_count}`);
          return;
        }
        
        // Fallback: if we have conditions, try to estimate based on that
        if (automation.conditions && Array.isArray(automation.conditions)) {
          // Set a more realistic count based on number of conditions
          const conditionCount = automation.conditions.filter(c => c.condition_group === 'ALL').length;
          const estimatedCount = Math.max(25 * conditionCount, 50);
          
          setAutomation(prev => ({
            ...prev,
            audience: {
              ...(prev.audience || {}),
              estimated_count: estimatedCount
            }
          }));
          return;
        }
        
        // Last resort: use a reasonable placeholder
        console.log('Using placeholder value for audience size');
        const placeholderCount = 50; // Fixed value for consistency
        setAutomation(prev => ({
          ...prev,
          audience: {
            ...(prev.audience || {}),
            estimated_count: placeholderCount
          }
        }));
      } catch (err) {
        console.error('Error in audience size estimation:', err);
        // Make sure we always have some value to display
        setAutomation(prev => ({
          ...prev,
          audience: {
            ...(prev.audience || {}),
            estimated_count: 50 // Default to 50 on error
          }
        }));
      }
    };
    
    if (automation && automation.audience && !automation.audience.estimated_count) {
      getAudienceSize();
    }
  }, [automation, token]);
  
  // Fetch execution history
  useEffect(() => {
    const fetchExecutionHistory = async () => {
      if (!id) return;
      
      try {
        setExecutionsLoading(true);
        const response = await apiClient.get(`/executions`, {
          params: { automation_id: id },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          setExecutions(response.data);
          
          // Calculate metrics from execution data
          if (response.data.length > 0) {
            const emailsSent = response.data.reduce((sum, exec) => sum + (exec.recipient_count || 0), 0);
            
            setMetrics({
              emailsSent,
              openRate: 25, // Default values
              clickRate: 10,
              bounceRate: 2
            });
          }
        }
      } catch (err) {
        console.error('Error fetching execution history:', err);
        setExecutions([]);
      } finally {
        setExecutionsLoading(false);
      }
    };
    
    fetchExecutionHistory();
  }, [id, token]);
  
  // Handlers using original hook functions
  const handleToggleStatus = async () => {
    try {
      await toggleAutomation(id);
      setAutomation(prev => ({
        ...prev, 
        status: prev.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE'
      }));
    } catch (err) {
      console.error('Error toggling automation status:', err);
      setError('Failed to update automation status. Please try again.');
    }
  };
  
  const handleClone = async () => {
    try {
      const result = await cloneAutomation(id);
      if (result.success) {
        navigate('/automations');
      }
    } catch (err) {
      console.error('Error cloning automation:', err);
      setError('Failed to clone automation. Please try again.');
    }
  };
  
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this automation?')) {
      try {
        await deleteAutomation(id);
        navigate('/automations');
      } catch (err) {
        console.error('Error deleting automation:', err);
        setError('Failed to delete automation. Please try again.');
      }
    }
  };
  
  const handleManualExecution = async () => {
    try {
      setExecutionsLoading(true);
      
      const response = await apiClient.post(
        '/executions/manual', 
        {
          automation_id: id,
          test_mode: false,
          prevent_duplicates: Boolean(automation.prevent_duplicates)
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Refresh execution history
      const executionsResponse = await apiClient.get(`/executions`, {
        params: { automation_id: id },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (executionsResponse.data) {
        setExecutions(executionsResponse.data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error triggering manual execution:', err);
      setError('Failed to trigger automation execution. Please try again.');
    } finally {
      setExecutionsLoading(false);
    }
  };
  
  // Helper functions - preserved from original
  const timezone = 'Europe/London'; // or dynamically load per clinic if available

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return DateTime.fromISO(dateString, { zone: 'utc' })
      .setZone(timezone)
      .toFormat('dd MMM yyyy, HH:mm');
  };
  
  // Use the imported formatSchedule utility function
  // We're leaving this wrapper function to maintain API compatibility
  const formatAutomationSchedule = (automation) => {
    if (!automation) return 'N/A';
    
    // Transform automation object to what formatSchedule expects
    const schedule = {
      scheduleType: automation.schedule_type,
      scheduleInterval: automation.schedule_interval,
      scheduleTime: automation.schedule_time,
      scheduleDay: automation.schedule_day,
      scheduleDays: automation.schedule_days
    };
    
    // DEBUG: Log the actual time values being processed
    console.log(`🕒 DETAILS.JS - formatAutomationSchedule debug:`, {
      rawTime: automation.schedule_time,
      timezone: localStorage.getItem('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    
    // IMPORTANT: Pass isUTC=true to ensure time is properly converted from UTC to local time
    // The raw schedule_time from DB is in UTC (07:00:00), so we need to convert it
    return formatSchedule(schedule, true);
  };
  
  // Format a single condition in a human-readable way (preserved from original)
  const formatConditionForDisplay = (recordType, fieldName, operator, value) => {
    if (!recordType || !fieldName || !operator) {
      return null;
    }
    
    // Map record types to more readable labels
    const recordTypeLabels = {
      'client': 'Client',
      'appointment': 'Appointment',
      'service': 'Service',
      'invoice': 'Invoice',
      'quote': 'Quote'
    };
    
    // Map field names to more readable labels
    const fieldLabels = {
      'first_name': 'First Name',
      'last_name': 'Last Name',
      'email': 'Email',
      'phone': 'Phone Number',
      'mobile': 'Mobile Number',
      'date_of_birth': 'Birth Date',
      'last_visit_date': 'Last Visit Date',
      'service_id': 'Service',
      'status': 'Status'
    };
    
    // Map operators to more readable text
    const operatorLabels = {
      'EQUALS': 'equals',
      'NOT_EQUALS': 'does not equal',
      'CONTAINS': 'contains',
      'NOT_CONTAINS': 'does not contain',
      'GREATER_THAN': 'is after',
      'LESS_THAN': 'is before',
      'IS_EMPTY': 'is empty',
      'IS_NOT_EMPTY': 'is not empty',
      'STARTS_WITH': 'starts with',
      'ENDS_WITH': 'ends with',
      'AFTER': 'is after'
    };
    
    const recordTypeLabel = recordTypeLabels[recordType.toLowerCase()] || recordType;
    const fieldLabel = fieldLabels[fieldName.toLowerCase()] || fieldName.replace(/_/g, ' ');
    const operatorLabel = operatorLabels[operator] || operator.toLowerCase().replace(/_/g, ' ');
    
    // Format the value based on field type
    let displayValue = value || '';
    if (operator === 'IS_EMPTY' || operator === 'IS_NOT_EMPTY') {
      // No need to show value for these operators
      return `${recordTypeLabel} ${fieldLabel} ${operatorLabel}`;
    } else if (fieldName.toLowerCase().includes('date')) {
      // Format date values
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          displayValue = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
      } catch (e) {
        // Keep original value if parsing fails
      }
    }
    
    return `${recordTypeLabel} ${fieldLabel} ${operatorLabel} ${displayValue}`;
  };
  
  // Improved audience summary function to handle different data structures
  const getAudienceSummary = (automation) => {
    if (!automation || !automation.audience) return 'No audience criteria set';
    
    // If we have a description, use it
    if (automation.audience.description) {
      return automation.audience.description;
    }
    
    // Format all conditions from groups into a bulleted list
    const allFormattedConditions = [];
    
    // FIRST CHECK: Look for conditions at the root level of the automation object
    if (automation.conditions && Array.isArray(automation.conditions)) {
      console.log('Found conditions at root level:', automation.conditions.length);
      
      // Process ALL conditions 
      const allConditions = automation.conditions
        .filter(c => c.condition_group === 'ALL')
        .map(c => {
          // Convert API field names to our internal recordType/fieldName structure
          const recordType = c.field_name.includes('service') ? 'Client' : 'Client';
          const fieldName = c.field_name === 'service_name' ? 'service type' : 
                           c.field_name === 'start_date' ? 'Last Visit Date' : c.field_name;
          
          const formatted = formatConditionForDisplay(
            recordType,
            fieldName,
            c.operator,
            c.value
          );
          if (formatted) {
            return `• ${formatted} (Required)`;
          }
          return null;
        })
        .filter(Boolean); // Remove nulls
      
      // Process ANY conditions
      const anyConditions = automation.conditions
        .filter(c => c.condition_group === 'ANY')
        .map(c => {
          // Convert API field names to our internal recordType/fieldName structure
          const recordType = c.field_name.includes('service') ? 'Client' : 'Client';
          const fieldName = c.field_name === 'service_name' ? 'service type' : 
                           c.field_name === 'start_date' ? 'Last Visit Date' : c.field_name;
          
          const formatted = formatConditionForDisplay(
            recordType,
            fieldName,
            c.operator,
            c.value
          );
          if (formatted) {
            return `• ${formatted} (Optional)`;
          }
          return null;
        })
        .filter(Boolean); // Remove nulls
      
      allFormattedConditions.push(...allConditions, ...anyConditions);
      
      if (allFormattedConditions.length > 0) {
        return allFormattedConditions.join('\n');
      }
    }
    
    // If we still have no conditions, check if maybe they're attached to audience
    if (automation.audience.conditions && Array.isArray(automation.audience.conditions)) {
      console.log('Found conditions in audience object:', automation.audience.conditions.length);
      
      // Process ALL conditions 
      const allConditions = automation.audience.conditions
        .filter(c => c.condition_group === 'ALL')
        .map(c => {
          const formatted = formatConditionForDisplay(
            c.recordType,
            c.fieldName,
            c.operator,
            c.value
          );
          if (formatted) {
            return `• ${formatted} (Required)`;
          }
          return null;
        })
        .filter(Boolean); // Remove nulls
      
      // Process ANY conditions
      const anyConditions = automation.audience.conditions
        .filter(c => c.condition_group === 'ANY')
        .map(c => {
          const formatted = formatConditionForDisplay(
            c.recordType,
            c.fieldName,
            c.operator,
            c.value
          );
          if (formatted) {
            return `• ${formatted} (Optional)`;
          }
          return null;
        })
        .filter(Boolean); // Remove nulls
      
      allFormattedConditions.push(...allConditions, ...anyConditions);
      
      if (allFormattedConditions.length > 0) {
        return allFormattedConditions.join('\n');
      }
    }
    
    // If we get here with no conditions found, check for other possible structures
    console.log('No standard audience structure found, audience data:', automation.audience);
    
    return 'All clients (no filters applied)';
  };
  
  const calculateNextRunTime = (automation) => {
    if (!automation || automation.status !== 'ACTIVE') return 'Not scheduled';
    
    // Return next run time if provided by API
    if (automation.next_run_time) {
      return formatDate(automation.next_run_time);
    }
    
    const userTimezone = localStorage.getItem('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    console.log(`🕒 DETAILS.JS - calculateNextRunTime using timezone: ${userTimezone}`);
    
    try {
      // Handle different schedule types
      switch (automation.schedule_type) {
        case 'DAILY': {
          // Convert the UTC time to local time first
          let scheduleTimeLocal = automation.schedule_time || '00:00:00';
          
          try {
            // Try to use our timeUtils functions
            const { convertUTCToLocalTime } = require('../../utils/timeUtils');
            scheduleTimeLocal = convertUTCToLocalTime(scheduleTimeLocal) || scheduleTimeLocal;
            console.log(`🕒 Converted UTC time ${automation.schedule_time} to local time ${scheduleTimeLocal}`);
          } catch (e) {
            // If timeUtils is not available, do basic conversion
            console.log('Could not use timeUtils, falling back to basic conversion');
            
            // Get the UTC time components
            const timeComponents = scheduleTimeLocal.split(':');
            const utcHours = parseInt(timeComponents[0], 10);
            const utcMinutes = parseInt(timeComponents[1] || 0, 10);
            
            // Try to use luxon for conversion
            try {
              const { DateTime } = require('luxon');
              const utcTime = DateTime.utc().set({ 
                hour: utcHours, 
                minute: utcMinutes, 
                second: 0, 
                millisecond: 0 
              });
              
              const localTime = utcTime.setZone(userTimezone);
              scheduleTimeLocal = localTime.toFormat('HH:mm:ss');
              console.log(`Manual conversion: ${automation.schedule_time} -> ${scheduleTimeLocal}`);
            } catch (luxonError) {
              console.log('Luxon not available, time might be incorrect');
            }
          }
          
          // Now use the local time for scheduling calculations
          const timeComponents = scheduleTimeLocal.split(':');
          const hours = parseInt(timeComponents[0], 10);
          const minutes = parseInt(timeComponents[1] || 0, 10);
          
          console.log(`🕒 Using local time components for next run calculation: ${hours}:${minutes}`);
          
          let nextRun = new Date();
          nextRun.setHours(hours, minutes, 0, 0);
          
          if (nextRun <= new Date()) {
            nextRun.setDate(nextRun.getDate() + 1);
          }
          
          const isToday = nextRun.getDate() === new Date().getDate() &&
                           nextRun.getMonth() === new Date().getMonth() &&
                           nextRun.getFullYear() === new Date().getFullYear();
          
          const tomorrow = new Date(new Date());
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          const isTomorrow = nextRun.getDate() === tomorrow.getDate() &&
                              nextRun.getMonth() === tomorrow.getMonth() &&
                              nextRun.getFullYear() === tomorrow.getFullYear();
          
          const timeStr = nextRun.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          
          if (isToday) {
            return `Today at ${timeStr}`;
          } else if (isTomorrow) {
            return `Tomorrow at ${timeStr}`;
          } else {
            const dateOptions = { weekday: 'long', month: 'short', day: 'numeric' };
            const dateStr = nextRun.toLocaleDateString('en-US', dateOptions);
            return `${dateStr} at ${timeStr}`;
          }
        }
        
        case 'WEEKLY': {
          const scheduleDay = automation.schedule_day || 'Monday';
          const scheduleDayMap = {
            'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4,
            'Friday': 5, 'Saturday': 6, 'Sunday': 0
          };
          
          const dayNum = scheduleDayMap[scheduleDay];
          const timeComponents = (automation.schedule_time || '00:00:00').split(':');
          const hours = parseInt(timeComponents[0], 10);
          const minutes = parseInt(timeComponents[1] || 0, 10);
          
          let nextRun = new Date();
          nextRun.setHours(hours, minutes, 0, 0);
          
          const currentDay = nextRun.getDay();
          let daysToAdd = dayNum - currentDay;
          if (daysToAdd < 0) daysToAdd += 7;
          if (daysToAdd === 0 && nextRun <= new Date()) daysToAdd = 7;
          
          nextRun.setDate(nextRun.getDate() + daysToAdd);
          
          const isToday = nextRun.getDate() === new Date().getDate() &&
                           nextRun.getMonth() === new Date().getMonth() &&
                           nextRun.getFullYear() === new Date().getFullYear();
          
          const isTomorrow = nextRun.getDate() === new Date(new Date()).getDate() + 1 &&
                              nextRun.getMonth() === new Date(new Date()).getMonth() &&
                              nextRun.getFullYear() === new Date(new Date()).getFullYear();
          
          const timeStr = nextRun.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          
          if (isToday) {
            return `Today at ${timeStr}`;
          } else if (isTomorrow) {
            return `Tomorrow at ${timeStr}`;
          } else {
            const dateOptions = { weekday: 'long', month: 'short', day: 'numeric' };
            const dateStr = nextRun.toLocaleDateString('en-US', dateOptions);
            return `${dateStr} at ${timeStr}`;
          }
        }
        
        default:
          return 'Scheduled';
      }
    } catch (err) {
      console.error('Error calculating next run time:', err);
      return 'Scheduled';
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button component={Link} to="/automations" variant="contained">
          Back to Automations
        </Button>
      </Box>
    );
  }
  
  if (!automation) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Automation not found. It may have been deleted.
        </Alert>
        <Button component={Link} to="/automations" variant="contained">
          Back to Automations
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {automation.title || 'Automation Details'}
        </Typography>
        
        <Stack direction="row" spacing={1}>
          <DevOnly>
            <Button
              variant="outlined"
              startIcon={automation.status === 'ACTIVE' ? <PauseIcon /> : <PlayIcon />}
              color={automation.status === 'ACTIVE' ? 'warning' : 'success'}
              onClick={handleToggleStatus}
              disabled={isUpdating}
            >
              {automation.status === 'ACTIVE' ? 'Pause' : 'Activate'}
            </Button>
          </DevOnly>
          
          <DevOnly>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              component={Link}
              to={`/automations/${id}/edit`}
            >
              Edit
            </Button>
          </DevOnly>
          
          <DevOnly>
            <Button
              variant="outlined"
              startIcon={<CloneIcon />}
              onClick={handleClone}
              disabled={isUpdating}
            >
              Clone
            </Button>
          </DevOnly>
          
          <DevOnly>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              color="error"
              onClick={handleDelete}
              disabled={isUpdating}
            >
              Delete
            </Button>
          </DevOnly>
        </Stack>
      </Box>
      
      {/* Status Card */}
      <Box sx={{ mb: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body1">Status:</Typography>
            <Chip 
              label={automation.status} 
              color={automation.status === 'ACTIVE' ? 'success' : 'default'} 
              size="medium" 
            />
            <Divider orientation="vertical" flexItem />
            <Typography variant="body1">Created: {formatDate(automation.created_at)}</Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="body1">Last Updated: {formatDate(automation.updated_at)}</Typography>
          </Stack>
        </Paper>
      </Box>
      
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item style={{ flex: 1 }}>
          {/* Schedule Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6">Schedule</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Type:</Typography>
                <Typography>{automation.schedule_type || 'Not set'}</Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Details:</Typography>
                <Typography>{formatAutomationSchedule(automation)}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Next Run:</Typography>
                <Typography>{calculateNextRunTime(automation)}</Typography>
              </Box>
            </CardContent>
          </Card>
          
          {/* Audience Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GroupIcon sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6">Audience</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Filter Name:</Typography>
                <Typography>
                  {automation.audience?.name || automation.audience?.filter_name || 'Default'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Criteria:</Typography>
                <Box sx={{ mt: 1 }}>
                  {getAudienceSummary(automation).split('\n').map((line, index) => (
                    <Typography 
                      key={index} 
                      sx={{ 
                        display: 'block',
                        mb: 0.5,
                        fontSize: '0.9rem',
                        fontFamily: line.includes('(Required)') ? 'inherit' : 'inherit',
                        fontWeight: line.includes('(Required)') ? 'medium' : 'normal',
                        color: line.includes('(Required)') ? 'text.primary' : 'text.secondary'
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Estimated Recipients:</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {automation.audience?.estimated_count 
                      ? automation.audience.estimated_count.toLocaleString() 
                      : 'Calculating...'}
                  </Typography>
                </Box>
                
                <Button 
                  variant="outlined" 
                  size="small"
                  component={Link}
                  to={`/automations/${id}/edit`}
                  startIcon={<EditIcon />}
                >
                  Edit Audience
                </Button>
              </Box>
            </CardContent>
          </Card>
          
          {/* Performance Metrics Card */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BarChartIcon sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6">Performance Metrics</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {performanceLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : error ? (
                <Typography color="error">Error loading performance metrics</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  <Box style={{ width: '50%', padding: '8px' }}>
                    <Typography variant="subtitle2" color="text.secondary">Total Emails Sent:</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                      {performance?.total_sent || 0}
                    </Typography>
                  </Box>
                  
                  <Box style={{ width: '50%', padding: '8px' }}>
                    <Typography variant="subtitle2" color="text.secondary">Open Rate:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {performance?.open_rate || 0}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(performance?.open_rate || 0, 100)} 
                        sx={{ ml: 2, flex: 1, height: 8, borderRadius: 5 }}
                        color="success"
                      />
                    </Box>
                  </Box>
                  
                  <Box style={{ width: '50%', padding: '8px' }}>
                    <Typography variant="subtitle2" color="text.secondary">Click Rate:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {performance?.click_rate || 0}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(performance?.click_rate || 0, 100)} 
                        sx={{ ml: 2, flex: 1, height: 8, borderRadius: 5 }}
                        color="info"
                      />
                    </Box>
                  </Box>
                  
                  <Box style={{ width: '50%', padding: '8px' }}>
                    <Typography variant="subtitle2" color="text.secondary">Bounce Rate:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {performance?.bounce_rate || 0}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(performance?.bounce_rate || 0, 100)} 
                        sx={{ ml: 2, flex: 1, height: 8, borderRadius: 5 }}
                        color="error"
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              
              {!performanceLoading && !error && performance?.total_sent > 0 && (
                <Box sx={{ mt: 3, textAlign: 'right' }}>
                  <Tooltip title="View detailed reports">
                    <Button 
                      variant="text" 
                      size="small" 
                      endIcon={<OpenInNewIcon />}
                      component={Link}
                      to={`/automations/${id}/reports`}
                    >
                      View Detailed Reports
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right Column */}
        <Grid item style={{ flex: 1 }}>
          {/* Email Content Preview Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ mr: 1 }} color="primary" />
                  <Typography variant="h6">Email Content</Typography>
                </Box>
                
                {automation.template?.id && (
                  <Tooltip title="Preview Template">
                    <IconButton 
                      component={Link} 
                      to={`/templates/${automation.template.id}/preview`}
                      size="small"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">From:</Typography>
                {automation.email_sender ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, fontSize: 'small' }} />
                    <Typography>{automation.email_sender?.display_name || 'Email Sender'}</Typography>
                    <Typography sx={{ ml: 1, color: 'text.secondary' }}>
                      ({automation.email_sender?.email || 'sender@email.com'})
                    </Typography>
                  </Box>
                ) : (
                  <Typography color="text.secondary" variant="body2">Default clinic sender</Typography>
                )}
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Subject:</Typography>
                <Typography>{automation.email_subject || 'No subject'}</Typography>
              </Box>
              
              {/* Use the EmailPreview component */}
              <EmailPreview htmlContent={automation.template?.html_content || ''} />
              
              <Button 
                variant="outlined" 
                component={Link} 
                to={`/automations/${id}/edit`}
                startIcon={<EditIcon />}
              >
                Edit Email Content
              </Button>
            </CardContent>
          </Card>
          
          {/* Execution History Card */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HistoryIcon sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6">Execution History</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {executionsLoading ? (
                <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                  <LinearProgress />
                </Box>
              ) : executions && executions.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Recipients</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {executions.map((execution) => (
                        <TableRow key={execution.id}>
                          <TableCell>{formatDate(execution.execution_time)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={execution.status} 
                              color={execution.status === 'SUCCESS' ? 'success' : 
                                     execution.status === 'IN_PROGRESS' ? 'info' : 'error'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>{execution.recipient_count || 0}</TableCell>
                          <TableCell>
                            <Tooltip title="View execution details">
                              <IconButton 
                                size="small" 
                                component={Link} 
                                to={`/automations/${id}/executions/${execution.id}`}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="text.secondary" gutterBottom>
                    No execution history available
                  </Typography>
                  
                  {/* Add Run Now Button */}
                  {automation?.status === 'ACTIVE' && (
                    <DevOnly>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={handleManualExecution}
                        startIcon={<PlayIcon />}
                      >
                        Run Now
                      </Button>
                    </DevOnly>
                  )}
                </Box>
              )}
              
              {executions.length > 5 && (
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Button 
                    variant="text" 
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    component={Link}
                    to={`/automations/${id}/executions`}
                  >
                    View All Executions
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AutomationDetails;